import React from "react";
import { T } from "services/lang/LanguageService";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { ContextTypes } from "api/model/dashboard/DashboardTab";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import { adjustReferenceTime } from "../../utils";

interface TabContextMenuProps {
  tabId: number;
}

const TabContextMenu = ({ tabId }: TabContextMenuProps) => {
  const tabContextConfig = useDashboardStore(state => state.getTabContextConfig(tabId) || {});
  const setTabContextConfig = useDashboardStore(state => state.setTabContextConfig);
  const triggerTabRefresh = useDashboardStore(state => state.triggerTabRefresh);

  const referenceTime = adjustReferenceTime(tabContextConfig.config).referenceTime || Date.now();
  const from = adjustReferenceTime(tabContextConfig.config).dateFrom || Date.now();
  const to = adjustReferenceTime(tabContextConfig.config).dateTo || Date.now();

  const handleInputChange = (idTab: number, newConfig: any) => {
    setTabContextConfig(idTab, newConfig);
  };

  return (
    <div className="flex flex-row flex-wrap gap-2">
      {tabContextConfig?.contextType === ContextTypes.RANGE_DATE_SELECTION && (
        <div className="flex flex-row flex-wrap gap-2">
          <div className="flex flex-column gap-2">
            <label htmlFor="Date From">{T("word.date-from")}</label>
            <div className="flex flex-row">
              <CalendarWrapper
                id="dateFrom"
                value={tabContextConfig.from ? new Date(tabContextConfig.from) : new Date(from)}
                onChange={e =>
                  handleInputChange(tabId, { ...tabContextConfig, from: e.toISOString() })
                }
                showTime={tabContextConfig.contextType === ContextTypes.RANGE_DATE_SELECTION}
                className="w-3 align-self-center"
                debounceTimeInMs={300}
              />
            </div>
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="Date To">{T("word.date-to")}</label>
            <div className="flex flex-row">
              <CalendarWrapper
                id="dateTo"
                value={tabContextConfig.to ? new Date(tabContextConfig.to) : new Date(to)}
                onChange={e =>
                  handleInputChange(tabId, { ...tabContextConfig, to: e.toISOString() })
                }
                showTime={tabContextConfig.contextType === ContextTypes.RANGE_DATE_SELECTION}
                className="w-3 align-self-center"
                debounceTimeInMs={300}
              />
            </div>
          </div>
        </div>
      )}
      {(tabContextConfig?.contextType === ContextTypes.SINGLE_DATE_SELECTION_WITH_CONFIG ||
        tabContextConfig?.contextType === ContextTypes.SINGLE_DATE_SELECTION) && (
        <div className="flex flex-column gap-2">
          <label htmlFor="referenceTime">{T("load-conf.reference-time")}</label>
          <div className="flex flex-row">
            <CalendarWrapper
              id="referenceTime"
              value={
                tabContextConfig.referenceTime
                  ? new Date(tabContextConfig.referenceTime)
                  : new Date(referenceTime)
              }
              onChange={e =>
                handleInputChange(tabId, {
                  ...tabContextConfig,
                  referenceTime: e.toISOString(),
                })
              }
              className="w-3 align-self-center"
              debounceTimeInMs={300}
            />
          </div>
        </div>
      )}
      {tabContextConfig?.contextType === ContextTypes.YEARS_SELECTION && (
        <div className="flex flex-column gap-2">
          <label htmlFor="years">{T("time.years")}</label>
          <div className="flex flex-row">
            <CalendarWrapper
              id="years"
              view="year"
              value={tabContextConfig.from ? new Date(tabContextConfig.from) : new Date()}
              onChange={e => {
                const selectedYear = e.getFullYear();
                const newFrom = new Date(selectedYear, 0, 1).toISOString();
                const newTo = new Date(selectedYear + 1, 0, 1);
                newTo.setMinutes(newTo.getMinutes() - 1);                

                handleInputChange(tabId, {
                  ...tabContextConfig,
                  from: newFrom || tabContextConfig.from,
                  to: newTo || tabContextConfig.to,
                });
              }}
              dateFormat="yy"
              className="w-3 align-self-center"
              debounceTimeInMs={300}
              showTime={false}
            />
          </div>
        </div>
      )}
      <Button
        icon="pi pi-play"
        tooltip={T("action.fetch-data")}
        tooltipOptions={{ position: "bottom" }}
        onClick={() => triggerTabRefresh()}
        className="p-button-success align-self-end"
      />
    </div>
  );
};

export default TabContextMenu;
